import { useEffect, useState } from 'react';
import { FileText, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import PrdDocumentPreview from '@/components/PrdDocumentPreview';
import { cn } from '@/lib/utils';

/**
 * Opens PRD preview in a scrollable modal.
 * @param {'link' | 'button'} triggerVariant
 */
export default function PrdDocumentModal({
  sessionId,
  className,
  triggerVariant = 'link',
  modalDescription,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [sessionId]);

  if (!sessionId) {
    return null;
  }

  const description =
    modalDescription ??
    'Review the product requirements document for this session.';

  const openModal = () => setOpen(true);

  return (
    <>
      {triggerVariant === 'button' ? (
        <Button
          type="button"
          onClick={openModal}
          className={cn(
            'w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-md hover:from-amber-700 hover:to-orange-700',
            className
          )}
        >
          <FileText className="mr-2 h-4 w-4" />
          View PRD document
        </Button>
      ) : (
        <button
          type="button"
          onClick={openModal}
          className={cn(
            'inline-flex items-center gap-1.5 text-sm font-medium text-amber-800 underline-offset-4 hover:text-amber-950 hover:underline',
            className
          )}
        >
          <FileText className="h-4 w-4 shrink-0" />
          View PRD document
          <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[min(92vh,900px)] w-[min(96vw,56rem)] max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50/80 px-6 py-4 text-left">
            <DialogTitle className="flex items-center gap-2 text-amber-950">
              <FileText className="h-5 w-5" />
              Product requirements (PRD)
            </DialogTitle>
            <DialogDescription className="text-amber-900/80">{description}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <PrdDocumentPreview key={sessionId} sessionId={sessionId} active={open} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
